import { useMemo } from "react";
import { Task } from "@common/types/task";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useOrganization } from "@api/organization.api";
import { useEntitiesById } from "@hooks/useEntities";
import { HStack } from "@design-system/HStack/HStack";
import { UserAvatar } from "@components/avatar/UserAvatar";
import {
    AvatarGroup,
    type AvatarGroupSize,
} from "@design-system/Avatar/AvatarGroup";
import { AvatarTooltip } from "@components/avatar/AvatarTooltip";
import { OrganizationAvatar } from "@components/avatar/OrganizationAvatar";
import { HideIcon } from "@icons";
import type { Organization } from "@common/types/organization";
import { WorkspaceMember } from "@common/types/workspace";
import {
    ClientVisibilityReason,
    getTaskClientVisibility,
} from "./taskClientVisibility";
import styles from "./VisibilityCell.module.scss";

const ENTITY_TOOLTIP_LIMIT = 3;

interface VisibleClient {
    member: {
        user: Pick<
            WorkspaceMember["user"],
            "id" | "name" | "email" | "avatar" | "iconColor"
        >;
    };
    reason: Extract<ClientVisibilityReason, { visible: true }>["reason"];
}

const formatEntitySummary = (
    resolvedNames: string[],
    totalCount: number,
): string => {
    if (resolvedNames.length === 0) {
        return totalCount === 1 ? "1 entity" : `${totalCount} entities`;
    }
    const shown = resolvedNames.slice(0, ENTITY_TOOLTIP_LIMIT);
    const remaining = totalCount - shown.length;
    const list = shown.join(", ");
    return remaining > 0 ? `${list} +${remaining} more` : list;
};

const getClientName = (client: VisibleClient) =>
    client.member.user.name ?? client.member.user.email;

const buildTooltip = (
    client: VisibleClient,
    taskOrganizationId: string | null,
    entitySummary: string,
): string => {
    const name = getClientName(client);
    switch (client.reason) {
        case "admin":
            return taskOrganizationId !== null
                ? `${name} - workspace admin (assigned to this task)`
                : `${name} - workspace admin`;
        case "assignee":
            return taskOrganizationId !== null
                ? `${name} - assigned to this task (overrides org scope)`
                : `${name} - assigned to this task`;
        case "creator":
            return `${name} - created this task`;
        case "all_entities":
            return `${name} - has access to ${entitySummary}`;
    }
};

/** What `TaskVisibilityContent` shows for a task, from `useTaskVisibility`. */
export interface TaskVisibilityState {
    taskOrganizationId: string | null;
    visibleClients: VisibleClient[];
    organization:
        | Pick<Organization, "id" | "name" | "logoUrl">
        | null
        | undefined;
    entitySummary: string;
}

/**
 * Which clients can see a task and the organisation it's scoped to, or null
 * when there's nothing to show: an org-scoped task no client can see, while
 * its organisation is loading or failed to load.
 */
export const useTaskVisibility = (task: Task): TaskVisibilityState | null => {
    const { data: workspaceMembers = [] } = useCurrentWorkspaceMembers();
    const { data: organization } = useOrganization();
    const entitiesById = useEntitiesById();

    const visibleClients: VisibleClient[] = useMemo(
        () =>
            workspaceMembers
                .filter((member) => member.organizationId === null)
                .reduce<VisibleClient[]>((acc, member) => {
                    const result = getTaskClientVisibility(task, member);
                    if (result.visible) {
                        acc.push({ member, reason: result.reason });
                    }
                    return acc;
                }, []),
        [task, workspaceMembers],
    );

    const entitySummary = useMemo(() => {
        const ids = task.entityIds ?? [];
        const names = ids
            .map((id) => entitiesById.get(id)?.name)
            .filter((n): n is string => !!n);
        return formatEntitySummary(names, ids.length);
    }, [task.entityIds, entitiesById]);

    const taskOrganizationId = task.organizationId;
    if (
        taskOrganizationId !== null &&
        !organization &&
        visibleClients.length === 0
    ) {
        return null;
    }
    return { taskOrganizationId, visibleClients, organization, entitySummary };
};

export const NOT_VISIBLE_TO_CLIENTS_LABEL = "Not visible to any client";

/** Whether no client can see the task and it isn't scoped to an organization. */
export const isTaskHiddenFromClients = ({
    taskOrganizationId,
    visibleClients,
}: TaskVisibilityState) =>
    taskOrganizationId === null && visibleClients.length === 0;

type VisibilityStackItem =
    | {
          kind: "organization";
          organization: NonNullable<TaskVisibilityState["organization"]>;
      }
    | { kind: "client"; client: VisibleClient };

/** Clients shown before the +N chip, after the organization. */
const MAX_VISIBLE_CLIENTS = 3;

const getStackItemKey = (item: VisibilityStackItem) =>
    item.kind === "organization"
        ? `organization:${item.organization.id}`
        : item.client.member.user.id;

const getStackItemName = (item: VisibilityStackItem) =>
    item.kind === "organization"
        ? item.organization.name
        : getClientName(item.client);

const getStackItems = ({
    taskOrganizationId,
    organization,
    visibleClients,
}: TaskVisibilityState): VisibilityStackItem[] => {
    const clientItems = visibleClients.map((client): VisibilityStackItem => ({
        kind: "client",
        client,
    }));
    return taskOrganizationId !== null && organization
        ? [{ kind: "organization", organization }, ...clientItems]
        : clientItems;
};

interface TaskVisibilityContentProps {
    visibility: TaskVisibilityState;
    /** @default "xs" */
    size?: AvatarGroupSize;
    /**
     * Each avatar's tooltip from its name, e.g. a board card's
     * "Visibility · Acme Co". @default why it sees the task
     */
    getAvatarTooltip?: (name: string) => string;
}

/**
 * Renders `useTaskVisibility`'s state as one avatar stack: the organization
 * an org-scoped task belongs to first, then each client who can see it. Each
 * avatar's tooltip says why: the organization's scope, or the client's
 * access (entities, assignee, creator, admin). A task no client can see and
 * no organization scopes shows a crossed-eye icon instead.
 */
export const TaskVisibilityContent = ({
    visibility,
    size = "xs",
    getAvatarTooltip,
}: Readonly<TaskVisibilityContentProps>) => {
    const { taskOrganizationId, visibleClients, entitySummary } = visibility;

    if (isTaskHiddenFromClients(visibility)) {
        return (
            <span
                className={styles.hiddenIcon}
                title={NOT_VISIBLE_TO_CLIENTS_LABEL}
            >
                <HideIcon />
            </span>
        );
    }

    const items = getStackItems(visibility);
    return (
        <AvatarGroup
            items={items}
            getKey={getStackItemKey}
            getLabel={getStackItemName}
            renderAvatar={(item) =>
                item.kind === "organization" ? (
                    <AvatarTooltip
                        content={
                            getAvatarTooltip?.(item.organization.name) ??
                            `Scoped to ${item.organization.name} - visible to org members only; non-org users can only see this if they are the assignee`
                        }
                    >
                        <OrganizationAvatar organization={item.organization} />
                    </AvatarTooltip>
                ) : (
                    <AvatarTooltip
                        content={
                            getAvatarTooltip?.(getClientName(item.client)) ??
                            buildTooltip(
                                item.client,
                                taskOrganizationId,
                                entitySummary,
                            )
                        }
                    >
                        <UserAvatar user={item.client.member.user} />
                    </AvatarTooltip>
                )
            }
            max={items.length - visibleClients.length + MAX_VISIBLE_CLIENTS}
            size={size}
        />
    );
};

/**
 * Which non-org workspace members (clients) can see a task, plus the owning
 * organisation when the task is org-scoped; nothing when `useTaskVisibility`
 * has nothing to show. List's Visibility column shows it.
 *
 * The visibility logic mirrors the backend via `getTaskClientVisibility`.
 */
export const TaskVisibility = ({ task }: { task: Task }) => {
    const visibility = useTaskVisibility(task);
    return (
        visibility && (
            <HStack align="center">
                <TaskVisibilityContent visibility={visibility} />
            </HStack>
        )
    );
};
